/**
 * Payer Connections — API surface (AC-2, AC-4, AC-9).
 *
 * Generated reads only: no enrollment mutation or sync-all endpoint. The
 * evidence aggregate delegates every transport read to the generated endpoint
 * while retaining a bounded, organization-scoped outcome across app remounts.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import type {
  GetV2AdminOrgsByOrganizationIdEnrollmentsStatusApiArg,
  LiveEnrollmentStatusResponse,
  PayerReceiptEvidenceResponse,
} from '@store/generated/enrollments';
import { enhancedApi as enrollmentsGeneratedApi } from '@store/generated/enrollments';
import { useAppDispatch } from '@store/hooks';

import { evidencePayerIdBatches, type PayerLoadFailure, payerLoadFailure } from '../progressive';

const MOUNT_FRESHNESS_MS = 30_000;

export interface PayerOrganizationEvidenceArgs {
  organizationId: string;
  evidenceSignature: string;
  payerIds: string[];
}

export interface PayerOrganizationEvidenceResult {
  completedSignature: string | null;
  evidence: PayerReceiptEvidenceResponse[];
  failure: PayerLoadFailure | null;
  isLoading: boolean;
  requestedSignature: string;
}

interface PayerOrganizationStatusResult {
  failure: PayerLoadFailure | null;
  status: LiveEnrollmentStatusResponse | undefined;
}

function mergeEvidence(
  previous: PayerReceiptEvidenceResponse[],
  current: PayerReceiptEvidenceResponse[],
): PayerReceiptEvidenceResponse[] {
  const byPayerId = new Map<string, PayerReceiptEvidenceResponse>();
  for (const item of [...previous, ...current]) {
    const payerId = item.payer_id.trim();
    const key = payerId.toLowerCase();
    if (!key) continue;
    const held = byPayerId.get(key);
    if (held?.has_received_claim === true && item.has_received_claim !== true) continue;
    byPayerId.set(key, { ...item, payer_id: payerId });
  }
  return [...byPayerId.values()];
}

interface EvidenceBatchResult {
  evidence: PayerReceiptEvidenceResponse[];
  failure: PayerLoadFailure | null;
}

async function readEvidenceBatch(
  api: Pick<BaseQueryApi, 'dispatch' | 'signal'>,
  organizationId: string,
  payerIds: string[],
): Promise<EvidenceBatchResult> {
  const request = api.dispatch(
    enrollmentsGeneratedApi.endpoints.postV2AdminOrgsByOrganizationIdEnrollmentsEvidence.initiate(
      {
        organizationId,
        payerEvidenceRequest: { payer_ids: payerIds },
      },
      { track: false },
    ),
  );
  const abort = () => request.abort();
  api.signal.addEventListener('abort', abort, { once: true });
  try {
    const response = await request.unwrap();
    return { evidence: response.evidence ?? [], failure: null };
  } catch (error) {
    return {
      evidence: [],
      failure: api.signal.aborted ? null : payerLoadFailure(error),
    };
  } finally {
    api.signal.removeEventListener('abort', abort);
    request.reset();
  }
}

const evidenceWalks = new Map<string, AbortController>();

function startEvidenceWalk(organizationId: string): AbortController {
  evidenceWalks.get(organizationId)?.abort();
  const controller = new AbortController();
  evidenceWalks.set(organizationId, controller);
  return controller;
}

function endEvidenceWalk(organizationId: string, controller: AbortController): void {
  if (evidenceWalks.get(organizationId) === controller) evidenceWalks.delete(organizationId);
}

function cancelEvidenceWalk(organizationId: string): void {
  evidenceWalks.get(organizationId)?.abort();
  evidenceWalks.delete(organizationId);
}

// Match Stedi connection mutations' existing tags so mounted enrollment reads
// refresh as soon as the selected organization connects or reconnects.
export const payerConnectionsApi = enrollmentsGeneratedApi
  .enhanceEndpoints({
    addTagTypes: ['StediConnection'],
    endpoints: {
      getV2ProviderEnrollmentsLive: {
        providesTags: [{ type: 'StediConnection', id: 'PROVIDER' }],
      },
      getV2AdminOrgsByOrganizationIdEnrollmentsLive: {
        providesTags: (_result, _error, arg) => [
          { type: 'StediConnection', id: arg.organizationId },
        ],
      },
      getV2AdminOrgsByOrganizationIdEnrollmentsStatus: {
        providesTags: (_result, _error, arg) => [
          { type: 'StediConnection', id: arg.organizationId },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: build => ({
      getPayerOrganizationStatus: build.query<
        PayerOrganizationStatusResult,
        GetV2AdminOrgsByOrganizationIdEnrollmentsStatusApiArg
      >({
        queryFn: async (args, api) => {
          const request = api.dispatch(
            enrollmentsGeneratedApi.endpoints.getV2AdminOrgsByOrganizationIdEnrollmentsStatus.initiate(
              args,
              { forceRefetch: true, subscribe: false },
            ),
          );
          const abort = () => request.abort();
          api.signal.addEventListener('abort', abort, { once: true });
          try {
            return { data: { failure: null, status: await request.unwrap() } };
          } catch (error) {
            return { data: { failure: payerLoadFailure(error), status: undefined } };
          } finally {
            api.signal.removeEventListener('abort', abort);
            request.unsubscribe();
          }
        },
        merge: (cached, incoming) => {
          cached.failure = incoming.failure;
          cached.status = incoming.status ?? cached.status;
        },
        providesTags: (_result, _error, arg) => [
          { type: 'StediConnection', id: arg.organizationId },
        ],
      }),
      getPayerOrganizationEvidence: build.query<
        PayerOrganizationEvidenceResult,
        PayerOrganizationEvidenceArgs
      >({
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}(${queryArgs.organizationId})`,
        forceRefetch: ({ currentArg, previousArg }) =>
          currentArg?.evidenceSignature !== previousArg?.evidenceSignature,
        keepUnusedDataFor: 60,
        queryFn: async ({ organizationId, evidenceSignature, payerIds }, api) => {
          // A new status snapshot or explicit Retry supersedes an older walk.
          cancelEvidenceWalk(organizationId);
          const batches = evidencePayerIdBatches(payerIds);
          const first = await readEvidenceBatch(api, organizationId, batches[0] ?? []);
          const complete = batches.length <= 1;
          return {
            data: {
              completedSignature: complete ? evidenceSignature : null,
              evidence: mergeEvidence([], first.evidence),
              failure: first.failure,
              isLoading: !complete,
              requestedSignature: evidenceSignature,
            },
          };
        },
        merge: (cached, incoming) => {
          const previousEvidence =
            cached.requestedSignature === incoming.requestedSignature
              ? cached.evidence
              : cached.evidence.filter(item => item.has_received_claim === true);
          cached.completedSignature = incoming.completedSignature;
          cached.evidence = mergeEvidence(previousEvidence, incoming.evidence);
          cached.failure = incoming.failure;
          cached.isLoading = incoming.isLoading;
          cached.requestedSignature = incoming.requestedSignature;
        },
        async onQueryStarted(
          { evidenceSignature, organizationId, payerIds },
          // eslint-disable-next-line @typescript-eslint/unbound-method
          { dispatch, getCacheEntry, queryFulfilled, requestId, updateCachedData },
        ) {
          try {
            await queryFulfilled;
          } catch {
            return;
          }

          const batches = evidencePayerIdBatches(payerIds);
          if (batches.length <= 1) return;
          const walk = startEvidenceWalk(organizationId);
          try {
            for (const payerIdsBatch of batches.slice(1)) {
              const batch = await readEvidenceBatch(
                { dispatch, signal: walk.signal },
                organizationId,
                payerIdsBatch,
              );
              if (walk.signal.aborted || getCacheEntry().requestId !== requestId) return;
              updateCachedData(cached => {
                cached.evidence = mergeEvidence(cached.evidence, batch.evidence);
                cached.failure = batch.failure ?? cached.failure;
              });
            }
            updateCachedData(cached => {
              cached.completedSignature = evidenceSignature;
              cached.isLoading = false;
            });
          } finally {
            endEvidenceWalk(organizationId, walk);
          }
        },
      }),
    }),
  });

/**
 * Keep rejected status reads stable across the delayed persistence remount.
 * Fulfilled data still refreshes after 30 seconds, and Retry always forces a
 * transport read.
 */
export function usePayerOrganizationStatusQuery(
  args: GetV2AdminOrgsByOrganizationIdEnrollmentsStatusApiArg,
) {
  const result = payerConnectionsApi.useGetPayerOrganizationStatusQuery(args, {
    refetchOnMountOrArgChange: MOUNT_FRESHNESS_MS / 1_000,
  });
  return {
    ...result,
    data: result.data?.status,
    error: result.data?.failure ?? result.error,
  };
}

export function usePayerOrganizationEvidenceQuery(
  args: PayerOrganizationEvidenceArgs,
  options: { skip: boolean },
) {
  const dispatch = useAppDispatch();
  const result = payerConnectionsApi.useGetPayerOrganizationEvidenceQuery(args, options);

  useEffect(() => {
    if (!options.skip) return;
    cancelEvidenceWalk(args.organizationId);
    const running = dispatch(
      payerConnectionsApi.util.getRunningQueryThunk('getPayerOrganizationEvidence', args) as never,
    ) as { abort: () => void } | undefined;
    running?.abort();
    dispatch(
      payerConnectionsApi.util.updateQueryData('getPayerOrganizationEvidence', args, cached => {
        cached.completedSignature = cached.requestedSignature;
        cached.isLoading = false;
      }),
    );
  }, [args, dispatch, options.skip]);

  return result;
}

/** Cancel evidence walks only when an organization leaves the selected page. */
export function useCancelDepartedPayerEvidence(organizationIds: string[]): void {
  const dispatch = useAppDispatch();
  const previousIds = useRef<Set<string>>(new Set());
  const stableIds = useMemo(() => new Set(organizationIds), [organizationIds]);

  useEffect(() => {
    for (const organizationId of previousIds.current) {
      if (!stableIds.has(organizationId)) {
        cancelEvidenceWalk(organizationId);
        const running = dispatch(
          payerConnectionsApi.util.getRunningQueryThunk('getPayerOrganizationEvidence', {
            evidenceSignature: '',
            organizationId,
            payerIds: [],
          }) as never,
        ) as { abort: () => void } | undefined;
        running?.abort();
        dispatch(
          payerConnectionsApi.util.updateQueryData(
            'getPayerOrganizationEvidence',
            { evidenceSignature: '', organizationId, payerIds: [] },
            cached => {
              cached.completedSignature = cached.requestedSignature;
              cached.isLoading = false;
            },
          ),
        );
      }
    }
    previousIds.current = stableIds;
  }, [dispatch, stableIds]);

  useEffect(() => {
    const mountedPathname = window.location.pathname;
    return () => {
      // DelayedPersistGate can withhold its replacement children while scoped
      // persistence rehydrates. The URL is stable across that remount, whereas
      // a real route departure updates it before this cleanup task runs.
      queueMicrotask(() => {
        if (window.location.pathname === mountedPathname) return;
        for (const organizationId of previousIds.current) {
          cancelEvidenceWalk(organizationId);
          const running = dispatch(
            payerConnectionsApi.util.getRunningQueryThunk('getPayerOrganizationEvidence', {
              evidenceSignature: '',
              organizationId,
              payerIds: [],
            }) as never,
          ) as { abort: () => void } | undefined;
          running?.abort();
          dispatch(
            payerConnectionsApi.util.updateQueryData(
              'getPayerOrganizationEvidence',
              { evidenceSignature: '', organizationId, payerIds: [] },
              cached => {
                cached.completedSignature = cached.requestedSignature;
                cached.isLoading = false;
              },
            ),
          );
        }
      });
    };
  }, [dispatch]);
}

export const {
  useGetV2ProviderEnrollmentsLiveQuery: usePayerConnectionsLiveQuery,
  useGetV2AdminOrgsByOrganizationIdEnrollmentsLiveQuery: useAdminPayerConnectionsLiveQuery,
  useGetV2AdminEnrollmentsOrganizationsQuery: usePayerLoadOrganizationsQuery,
} = payerConnectionsApi;
